import { cognitoUserManager, cognitoUserManagerSettings, getCognitoUserInfoUrl } from '@/configs/cognitoAuthConfig';
import { ACCESS_TOKEN_EXPIRATION_BUFFER, ACCESS_TOKEN_NAME, TOKEN_EXPIRATION_TIME } from '@/constants';
import { handleError } from '@/helpers';
import { getDataFromLs, removeDataFromLs, setDataToLs } from '@/helpers/localStorage';
import { User, type UserProfile } from 'oidc-client-ts';
import type { UserProfileResponse } from '@/types';

type TokenInfo = {
  access_token: string;
  expires_at: number;
};

type SignInRedirectPayload = {
  state?: Record<string, string>;
};

const LOGOUT_INTENT_STORAGE_KEY = 'auth-logout-intent';
const ADMIN_PRODUCT_REDIRECT_INTENT_STORAGE_KEY = 'admin-product-redirect-intent';

const INVITATION_TOKEN_DEFAULT_TTL_MS = 24 * 60 * 60 * 1000;

const getJwtExpirationTimeMs = (accessToken: string): number | null => {
  try {
    const parts = accessToken.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const payload = JSON.parse(atob(padded)) as { exp?: number };

    if (typeof payload.exp !== 'number') {
      return null;
    }

    return payload.exp * 1000;
  } catch {
    return null;
  }
};

const decodeJwtPayload = (accessToken: string): Record<string, unknown> | null => {
  try {
    const parts = accessToken.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const payload = JSON.parse(atob(padded)) as Record<string, unknown>;

    if (!payload || typeof payload !== 'object') {
      return null;
    }

    return payload;
  } catch {
    return null;
  }
};

const getStringClaim = (payload: Record<string, unknown>, key: string): string => {
  const value = payload[key];
  return typeof value === 'string' ? value.trim() : '';
};

const toAbsoluteUrl = ({ baseUrl, path }: { baseUrl: string; path: string }): string => {
  const normalizedBaseUrl = baseUrl.replace(/\/+$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${normalizedBaseUrl}${normalizedPath}`;
};

export class AuthService {
  private readonly oidcStorageKey = `oidc.user:${cognitoUserManagerSettings.authority}:${cognitoUserManagerSettings.client_id}`;

  private isRefreshing = false;
  private refreshPromise: Promise<TokenInfo | null> | null = null;

  getAccessToken(): string | null {
    return getDataFromLs(ACCESS_TOKEN_NAME) || null;
  }

  getAccessTokenExpiresAtMs(): number | null {
    const expiresAt = getDataFromLs(TOKEN_EXPIRATION_TIME);
    if (!expiresAt) {
      return null;
    }

    const parsed = parseInt(expiresAt);
    if (Number.isNaN(parsed)) {
      return null;
    }

    return parsed;
  }

  setTokens({ access_token, expires_at }: TokenInfo): void {
    setDataToLs(ACCESS_TOKEN_NAME, access_token);
    setDataToLs(TOKEN_EXPIRATION_TIME, expires_at.toString());
  }

  applyInvitationAccessToken(accessToken: string): void {
    const expiresAtMs = getJwtExpirationTimeMs(accessToken) ?? Date.now() + INVITATION_TOKEN_DEFAULT_TTL_MS;
    this.setTokens({
      access_token: accessToken,
      expires_at: expiresAtMs,
    });
  }

  async applyImpersonationHandoff({
    accessToken,
    idToken,
    refreshToken,
    expiresIn,
    tokenType,
    scope,
  }: {
    accessToken: string;
    idToken?: string | null;
    refreshToken?: string | null;
    expiresIn?: number | null;
    tokenType?: string | null;
    scope?: string | null;
  }): Promise<void> {
    // Use the imperative TTL when present (Cognito returns expires_in in seconds);
    // fall back to the JWT exp claim, then to the invitation default.
    const expiresAtMs =
      typeof expiresIn === 'number' && expiresIn > 0
        ? Date.now() + expiresIn * 1000
        : (getJwtExpirationTimeMs(accessToken) ?? Date.now() + INVITATION_TOKEN_DEFAULT_TTL_MS);
    const expiresAtSeconds = Math.floor(expiresAtMs / 1000);

    const profile =
      (idToken ? this.getUserProfileFromIdToken(idToken) : null) ??
      this.getUserProfileFromAccessToken(accessToken) ??
      ({ sub: 'impersonated-user' } as UserProfile);

    const user = new User({
      access_token: accessToken,
      ...(idToken ? { id_token: idToken } : {}),
      ...(refreshToken ? { refresh_token: refreshToken } : {}),
      token_type: tokenType?.trim() || 'Bearer',
      ...(scope ? { scope } : {}),
      profile,
      expires_at: expiresAtSeconds,
    });

    // Persist into the OIDC userStore (sessionStorage by default) so oidc-client-ts
    // can drive automaticSilentRenew via the Cognito refresh_token grant.
    // getUser(true) loads the user into the UserManager's internal state, arms the
    // SilentRenewService timer based on expires_at, and fires addUserLoaded — which
    // our UserManagerEventsService mirrors into LS via setTokens().
    // During impersonation, signinSilent is wrapped to send
    // VITE_CONTROL_CENTER_COGNITO_CLIENT_ID (tokens were issued by that app client).
    await cognitoUserManager.storeUser(user);
    const loadedUser = await cognitoUserManager.getUser(true);
    if (loadedUser?.refresh_token) {
      cognitoUserManager.startSilentRenew();
    }

    // Also write the LS mirror eagerly. getUser(true) does this through the
    // addUserLoaded handler, but doing it here guarantees the synchronous
    // hasValidLocalAccessToken() check passes immediately on the next render.
    this.setTokens({
      access_token: accessToken,
      expires_at: expiresAtMs,
    });
  }

  clearTokens(): void {
    removeDataFromLs(ACCESS_TOKEN_NAME);
    removeDataFromLs(TOKEN_EXPIRATION_TIME);
  }

  setLogoutIntent(): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.sessionStorage.setItem(LOGOUT_INTENT_STORAGE_KEY, '1');
  }

  clearLogoutIntent(): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.sessionStorage.removeItem(LOGOUT_INTENT_STORAGE_KEY);
  }

  hasLogoutIntent(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    return window.sessionStorage.getItem(LOGOUT_INTENT_STORAGE_KEY) === '1';
  }

  setAdminProductRedirectIntent(): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.sessionStorage.setItem(ADMIN_PRODUCT_REDIRECT_INTENT_STORAGE_KEY, '1');
  }

  consumeAdminProductRedirectIntent(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    const hasIntent = window.sessionStorage.getItem(ADMIN_PRODUCT_REDIRECT_INTENT_STORAGE_KEY) === '1';
    if (!hasIntent) {
      return false;
    }

    window.sessionStorage.removeItem(ADMIN_PRODUCT_REDIRECT_INTENT_STORAGE_KEY);
    return true;
  }

  private clearOidcStorageByPrefix(storage: Storage): void {
    const keysToRemove: string[] = [];

    for (let index = 0; index < storage.length; index += 1) {
      const currentKey = storage.key(index);
      if (!currentKey) {
        continue;
      }

      if (currentKey.startsWith('oidc.')) {
        keysToRemove.push(currentKey);
      }
    }

    keysToRemove.forEach((key) => storage.removeItem(key));
  }

  async revokeRefreshToken(): Promise<void> {
    try {
      // Best-effort: Cognito's /oauth2/revoke invalidates the refresh token
      // server-side. Failures are expected for some handoff tokens and must not
      // block logout or surface UI errors.
      await cognitoUserManager.revokeTokens(['refresh_token']);
    } catch (_) {
      // Intentionally ignored — local session cleanup proceeds regardless.
    }
  }

  async clearLocalAuthSession({ skipTokenRevoke = false }: { skipTokenRevoke?: boolean } = {}): Promise<void> {
    // Revoke the refresh token before wiping local state — once removeUser()
    // runs, oidc-client-ts no longer has the token in memory to send to /revoke.
    if (!skipTokenRevoke) {
      await this.revokeRefreshToken();
    }

    this.clearTokens();

    if (typeof window === 'undefined') {
      return;
    }

    window.sessionStorage.removeItem(this.oidcStorageKey);
    window.localStorage.removeItem(this.oidcStorageKey);
    this.clearOidcStorageByPrefix(window.sessionStorage);
    this.clearOidcStorageByPrefix(window.localStorage);

    await Promise.allSettled([cognitoUserManager.removeUser(), cognitoUserManager.clearStaleState()]);
  }

  isTokenExpired(): boolean {
    const expiresAt = getDataFromLs(TOKEN_EXPIRATION_TIME);
    if (!expiresAt) return true;

    // Add buffer before expiration
    return Date.now() >= parseInt(expiresAt) - ACCESS_TOKEN_EXPIRATION_BUFFER;
  }

  getUserProfileFromJwt(token: string): UserProfile | null {
    const payload = decodeJwtPayload(token);
    if (!payload) {
      return null;
    }

    const givenName = getStringClaim(payload, 'given_name');
    const familyName = getStringClaim(payload, 'family_name');
    const email = getStringClaim(payload, 'email');
    const picture = getStringClaim(payload, 'picture');
    const sub = getStringClaim(payload, 'sub');
    const cognitoUsername = getStringClaim(payload, 'cognito:username');
    const username = getStringClaim(payload, 'username');
    const preferredUsername = getStringClaim(payload, 'preferred_username');

    const resolvedPreferredUsername = preferredUsername || username || cognitoUsername;

    if (!givenName && !familyName && !email && !resolvedPreferredUsername && !sub) {
      return null;
    }

    const profile = {
      ...(sub ? { sub } : {}),
      ...(email ? { email } : {}),
      ...(givenName ? { given_name: givenName } : {}),
      ...(familyName ? { family_name: familyName } : {}),
      ...(picture ? { picture } : {}),
      ...(resolvedPreferredUsername ? { preferred_username: resolvedPreferredUsername } : {}),
    } as unknown as UserProfile;

    return profile;
  }

  getUserProfileFromAccessToken(accessToken: string): UserProfile | null {
    return this.getUserProfileFromJwt(accessToken);
  }

  getUserProfileFromIdToken(idToken: string): UserProfile | null {
    return this.getUserProfileFromJwt(idToken);
  }

  hasOidcUserInSessionStorage(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    return Boolean(window.sessionStorage.getItem(this.oidcStorageKey));
  }

  async fetchUserProfileFromCognito({ accessToken }: { accessToken: string }): Promise<UserProfile | null> {
    try {
      const response = await fetch(getCognitoUserInfoUrl(), {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        return null;
      }

      const data = (await response.json()) as unknown;
      if (!data || typeof data !== 'object') {
        return null;
      }

      return data as UserProfile;
    } catch {
      return null;
    }
  }

  async fetchUserProfileFromApi({ accessToken }: { accessToken: string }): Promise<UserProfile | null> {
    try {
      const apiBaseUrl = import.meta.env.VITE_API_URL || '/api';
      const url = toAbsoluteUrl({ baseUrl: apiBaseUrl, path: '/admin/users/profile' });

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'access-token': accessToken,
        },
      });

      if (!response.ok) {
        return null;
      }

      const data = (await response.json()) as UserProfileResponse;
      if (!data || typeof data !== 'object') {
        return null;
      }

      const profile = {
        ...(data.id ? { sub: data.id } : {}),
        ...(data.email ? { email: data.email } : {}),
        ...(data.firstName ? { given_name: data.firstName } : {}),
        ...(data.lastName ? { family_name: data.lastName } : {}),
        ...(data.profileImageUrl ? { picture: data.profileImageUrl } : {}),
      } as unknown as UserProfile;

      return profile;
    } catch {
      return null;
    }
  }

  async refreshAccessToken(): Promise<TokenInfo | null> {
    // Prevent multiple simultaneous refresh attempts
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;
    this.refreshPromise = this.performTokenRefresh();

    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  private async performTokenRefresh(): Promise<TokenInfo | null> {
    try {
      const user = await cognitoUserManager.getUser();
      if (!user) {
        this.clearTokens();
        return null;
      }

      // Check if user has expired and needs refresh
      if (user.expired) {
        const refreshedUser = await cognitoUserManager.signinSilent();
        if (refreshedUser) {
          // Tokens will be automatically synced by the event handler
          const tokenInfo: TokenInfo = {
            access_token: refreshedUser.access_token,
            expires_at: (refreshedUser.expires_at || 0) * 1000, // Convert to milliseconds
          };
          return tokenInfo;
        } else {
          this.clearTokens();
          return null;
        }
      } else {
        // User is still valid, tokens are already synced by event handlers
        const tokenInfo: TokenInfo = {
          access_token: user.access_token,
          expires_at: (user.expires_at || 0) * 1000, // Convert to milliseconds
        };
        return tokenInfo;
      }
    } catch (error) {
      handleError(error);
      this.clearTokens();
      return null;
    }
  }

  async getValidAccessToken(): Promise<string | null> {
    try {
      const user = await cognitoUserManager.getUser();
      if (user) {
        if (user.expired) {
          const refreshedTokens = await this.refreshAccessToken();
          if (refreshedTokens?.access_token) {
            return refreshedTokens.access_token;
          }
          // Fall through to local storage (e.g. invitation token after a stale Cognito session).
        } else {
          const accessToken = user.access_token;
          const expiresAtMs = (user.expires_at || 0) * 1000;

          // Keep LS in sync with the active OIDC session.
          this.setTokens({
            access_token: accessToken,
            expires_at: expiresAtMs,
          });

          return accessToken;
        }
      }
    } catch (_) {
      // Fall through to local storage token when Cognito read fails.
    }

    const accessToken = this.getAccessToken();
    if (!accessToken) {
      return null;
    }

    if (this.isTokenExpired()) {
      const refreshedTokens = await this.refreshAccessToken();
      return refreshedTokens?.access_token || null;
    }

    return accessToken;
  }

  async signinRedirect({ state }: SignInRedirectPayload = {}) {
    try {
      if (state) {
        await cognitoUserManager.signinRedirect({ state });
        return;
      }

      await cognitoUserManager.signinRedirect();
    } catch (e) {
      handleError(e);
    }
  }

  async signinRedirectCallback() {
    try {
      return await cognitoUserManager.signinRedirectCallback();
    } catch (e) {
      handleError(e);
      return null;
    }
  }
}

export default new AuthService();
